import React, { useRef } from 'react';
import { IconButton, Stack } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { UnityViewer } from 'components/UnityViewer';
import ApsViewer from 'containers/ApsViewer';
import { UnityViewerContextProvider } from 'contexts/UnityViewerContext';
import useUnityViewer from 'hooks/useUnityViewer';

const CompareVirtualTourAPS = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Initialize Unity viewer hook to enable camera synchronization
  useUnityViewer('uniscan-compare');

  const handleMouseDown = (_e: React.MouseEvent) => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        const containerRect = container.getBoundingClientRect();
        const x = moveEvent.clientX - containerRect.left;
        const containerWidth = containerRect.width;

        const percentage = Math.max(10, Math.min(90, (x / containerWidth) * 100));

        const leftPanel = container.querySelector('.left-panel') as HTMLDivElement;
        const rightPanel = container.querySelector('.right-panel') as HTMLDivElement;

        if (leftPanel && rightPanel) {
          leftPanel.style.width = `${percentage}%`;
          rightPanel.style.width = `${100 - percentage}%`;
        }
      });
    };

    const onMouseUp = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <Stack height="100%" sx={{ flexDirection: 'column' }}>
      <Stack
        ref={containerRef}
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Stack
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Stack
            className="left-panel"
            sx={{
              height: '100%',
              width: '50%',
              transition: 'width 0.2s ease-out',
              overflow: 'hidden',
            }}
          >
            <UnityViewerContextProvider>
              <UnityViewer scanType="uniscan-compare" isEnabled={false} />
            </UnityViewerContextProvider>
          </Stack>

          <Stack
            sx={{
              width: '20px',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'col-resize',
              zIndex: 1000,
              backgroundColor: 'transparent',
            }}
            onMouseDown={handleMouseDown}
          >
            <IconButton
              size="small"
              sx={{
                backgroundColor: 'grey.300',
                '&:hover': { backgroundColor: 'grey.400' },
              }}
            >
              <DragIndicatorIcon />
            </IconButton>
          </Stack>

          <Stack
            className="right-panel"
            sx={{
              height: '100%',
              width: '50%',
              transition: 'width 0.2s ease-out',
              overflow: 'hidden',
            }}
          >
            <ApsViewer />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CompareVirtualTourAPS;
